/* eslint-disable react/prop-types */
import React, { useState } from 'react';
import { Button, Stack, Box, Typography, Divider, Popover } from '@mui/material';
import dayjs, { Dayjs } from 'dayjs';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import { addDays } from 'date-fns';
import { DayPicker, DateRange } from 'react-day-picker';
import constants from '../../shared/constants';
import '../../assets/scss/CustomDateRangePick.scss';
import CustomDateStyles from './CustomDateRangePickerStyles';

const options = [7, 30, 60, 90, 120, 150, 365];

interface CustomDateRangePickerProps {
    onDateChange?: (from: Dayjs, to: Dayjs) => void;
    label: string;
}

const CustomDateRangePicker: React.FC<CustomDateRangePickerProps> = ({ onDateChange, label }) => {
    const defaultSelected: DateRange = {
        from: new Date(dayjs().toDate()),
        to: addDays(new Date(dayjs().toDate()), 0)
    };
    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [range, setRange] = useState<DateRange | undefined>(defaultSelected);
		const [selectedOption, setSelectedOption] = useState<number | null>(null);

    const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleCloseMenu = () => {
        setAnchorEl(null);
    };

    const handleSave = () => {
        if (range && range.from) {
            onDateChange && onDateChange(dayjs(range.from), !range.to ? dayjs(range.from) : dayjs(range.to));
            handleCloseMenu();
        }
    };

    const handleOptionsClick = (option: number) => {
				setSelectedOption(option);
        const startDate = new Date(dayjs().subtract(option, 'day').toDate());
        const endDate = new Date(dayjs().toDate());
        setRange({ from: startDate, to: endDate });
    };

    return (
        <div>
            <Button
                color='secondary'
                size='small'
                onClick={handleOpenMenu}
                sx={CustomDateStyles.buttonCustomDateStyles}
                endIcon={<CalendarMonthIcon />}
            >
                {label}
            </Button>

            <Popover
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleCloseMenu}
            >
                <Stack spacing={2}  sx={{ padding: '5px', minWidth: '370px', border: '1px solid #D8D5FF', borderRadius:'10px' }}>
                    <Box alignItems='center' justifyContent='center' sx={{ display:'flex',padding: '5px', background: '#ffffff', colour: '#7660db' }}>
                        <Typography  sx={{ fontWeight: 'bold',color:'#7660db' }}>
                            {range ? dayjs(range.from).format(constants.DATE_FORMAT) : '__-__-____'} To {range ? !range.to ? dayjs(range.from).format(constants.DATE_FORMAT) : dayjs(range.to).format(constants.DATE_FORMAT) : '__-__-____'}
                        </Typography>
                    </Box>
                    <Divider />
                    <Stack direction='row' justifyContent='space-around'>
                        <DayPicker
                            id="test"
                            mode="range"
                            defaultMonth={new Date(dayjs().toDate())}
                            selected={range}
                            onSelect={setRange}
                            toDate={new Date()}
                            modifiersClassNames={{
                                selected: 'my-selected',
                                today: 'my-today',
                                range_start: 'my-selected-range-start',
                                range_middle: 'my-selected-range-middle',
                                range_end: 'my-selected-range-end',
                            }}
                            styles={{
                                caption: { fontFamily: 'sans-serif' },
                                day: { fontFamily: 'sans-serif', fontSize: '12px' }
                            }}
                            style={CustomDateStyles.date}
                        />
                        <Stack sx={{ alignSelf: 'end' }}>
                            {options.map((option) => (
                                <Button size='small' variant='outlined' key={option} style={selectedOption === option ? CustomDateStyles.selectedOption : CustomDateStyles.options} onClick={() => handleOptionsClick(option)}>
                                    Last {option} days
                                </Button>
                            ))}
                        </Stack>
                    </Stack>
                    <Divider />
                    <Stack spacing={1} sx={{ ...CustomDateStyles.range }}>
                        {!range && (
                            <Typography
                                sx={{ ...CustomDateStyles.valid }}
                            >
                                Please select a valid Date Range
                            </Typography>
                        )}
                        <Button variant="contained" color='secondary' size='small' disabled={!range} onClick={handleSave} >
                            OK
                        </Button>
                    </Stack>
                </Stack>
            </Popover>
        </div>
    );
};

export default CustomDateRangePicker;
